// apps/web/src/services/templateEngine.js
// Template Engine - Processes interactive templates with variables

/**
 * Template Variable Types:
 * - text: Free text input
 * - number: Numeric input
 * - date: Date picker
 * - dropdown: Select from options
 * - checkbox: Single boolean
 * - checkbox_group: Multiple selection
 */

/**
 * Process template with user-provided variable values
 * @param {Object} template - Template object with variables and task definitions
 * @param {Object} values - User-provided values for variables
 * @returns {Array} - Generated tasks ready to be created
 */
export function processTemplate(template, values) {
  if (!template.variables || template.variables.length === 0) {
    // Static template - return tasks as-is
    return template.tasks || [];
  }

  const processedTasks = [];

  for (const taskDef of template.tasks || []) {
    // Check condition if present
    if (taskDef.condition && !evaluateCondition(taskDef.condition, values)) {
      continue; // Skip this task
    }

    // Process task with variable substitution
    const task = {
      title: substituteVariables(taskDef.title, values),
      description: taskDef.description ? substituteVariables(taskDef.description, values) : '',
      status: taskDef.status || 'todo',
      due_date: taskDef.due_date ? processDueDate(taskDef.due_date, values) : null,
    };

    processedTasks.push(task);
  }

  return processedTasks;
}

/**
 * Substitute {{variable}} placeholders with actual values
 * @param {string} text - Text with placeholders
 * @param {Object} values - Variable values
 * @returns {string} - Text with substituted values
 */
function substituteVariables(text, values) {
  if (!text) return '';

  return text.replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
    const trimmed = varName.trim();

    // Handle expressions like {{team_size * 2}}
    if (trimmed.includes(' ')) {
      try {
        return evaluateExpression(trimmed, values);
      } catch (e) {
        return match; // Return original if expression fails
      }
    }

    // Simple variable substitution
    return values[trimmed] !== undefined ? values[trimmed] : match;
  });
}

/**
 * Evaluate condition expression
 * @param {string} condition - Condition string (e.g., "team_size > 1")
 * @returns {boolean} - Whether condition is true
 */
function evaluateCondition(condition, values) {
  try {
    // Replace variable names with their values
    let expr = condition;
    for (const [key, value] of Object.entries(values)) {
      const regex = new RegExp(`\\b${key}\\b`, 'g');
      if (typeof value === 'string') {
        expr = expr.replace(regex, `"${value}"`);
      } else if (typeof value === 'boolean') {
        expr = expr.replace(regex, value ? 'true' : 'false');
      } else if (Array.isArray(value)) {
        expr = expr.replace(regex, JSON.stringify(value));
      } else {
        expr = expr.replace(regex, String(value));
      }
    }

    // Safely evaluate the expression
    // eslint-disable-next-line no-new-func
    return new Function(`return ${expr}`)();
  } catch (e) {
    console.error('Error evaluating condition:', condition, e);
    return false;
  }
}

/**
 * Evaluate mathematical/logical expression
 */
function evaluateExpression(expr, values) {
  let evaluated = expr;
  for (const [key, value] of Object.entries(values)) {
    const regex = new RegExp(`\\b${key}\\b`, 'g');
    evaluated = evaluated.replace(regex, String(value));
  }

  // eslint-disable-next-line no-new-func
  return new Function(`return ${evaluated}`)();
}

/**
 * Process due date (can be relative like "deadline - 7 days")
 */
function processDueDate(dueDateExpr, values) {
  if (!dueDateExpr) return null;

  // If it's a variable reference
  if (dueDateExpr.startsWith('{{') && dueDateExpr.endsWith('}}')) {
    const varName = dueDateExpr.slice(2, -2).trim();
    return values[varName] || null;
  }

  // If it's an expression like "{{deadline}} - 7 days"
  if (dueDateExpr.includes('{{')) {
    const substituted = substituteVariables(dueDateExpr, values);
    // Parse relative date expressions
    return parseRelativeDate(substituted);
  }

  return dueDateExpr;
}

/**
 * Parse relative date expressions like "2024-12-31 - 7 days"
 */
function parseRelativeDate(expr) {
  const match = expr.match(/^(.+?)\s*([+-])\s*(\d+)\s*(days?|weeks?|months?)$/i);
  if (!match) return expr;

  const [, dateStr, operator, amount, unit] = match;
  const baseDate = new Date(dateStr);

  if (isNaN(baseDate.getTime())) return expr;

  const days = parseInt(amount) * (
    unit.startsWith('week') ? 7 :
    unit.startsWith('month') ? 30 :
    1
  );

  const multiplier = operator === '+' ? 1 : -1;
  baseDate.setDate(baseDate.getDate() + (days * multiplier));

  return baseDate.toISOString().split('T')[0];
}

/**
 * Validate user input against variable definition
 * @param {Object} variable - Variable definition
 * @param {*} value - User-provided value
 * @returns {Object} - { valid: boolean, error: string }
 */
export function validateVariable(variable, value) {
  // Required check
  if (variable.required && (value === null || value === undefined || value === '')) {
    return { valid: false, error: `${variable.label} is required` };
  }

  // Type-specific validation
  switch (variable.type) {
    case 'number':
      if (isNaN(value)) {
        return { valid: false, error: `${variable.label} must be a number` };
      }
      if (variable.min !== undefined && value < variable.min) {
        return { valid: false, error: `${variable.label} must be at least ${variable.min}` };
      }
      if (variable.max !== undefined && value > variable.max) {
        return { valid: false, error: `${variable.label} must be at most ${variable.max}` };
      }
      break;

    case 'text':
      if (variable.minLength && value.length < variable.minLength) {
        return { valid: false, error: `${variable.label} must be at least ${variable.minLength} characters` };
      }
      if (variable.maxLength && value.length > variable.maxLength) {
        return { valid: false, error: `${variable.label} must be at most ${variable.maxLength} characters` };
      }
      break;

    case 'date':
      if (value && isNaN(new Date(value).getTime())) {
        return { valid: false, error: `${variable.label} must be a valid date` };
      }
      break;
  }

  return { valid: true };
}

/**
 * Get default values for template variables
 * @param {Array} variables - Variable definitions
 * @returns {Object} - Default values
 */
export function getDefaultValues(variables) {
  const defaults = {};

  // Ensure variables is always an array
  const variablesArray = Array.isArray(variables) ? variables : [];

  for (const variable of variablesArray) {
    // Set sensible defaults based on type
    switch (variable.type) {
      case 'number':
        defaults[variable.id] = variable.default !== undefined ? variable.default : 0;
        break;
      case 'checkbox':
        defaults[variable.id] = variable.default !== undefined ? variable.default : false;
        break;
      case 'checkbox_group':
        // CRITICAL: Always ensure checkbox_group is an array
        if (variable.default !== undefined && Array.isArray(variable.default)) {
          defaults[variable.id] = variable.default;
        } else {
          defaults[variable.id] = [];
        }
        break;
      default:
        defaults[variable.id] = variable.default !== undefined ? variable.default : '';
    }
  }

  return defaults;
}
